import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import { WeekNavigator } from "../components/WeekNavigator";
import type {
  BasketPlanItem,
  BasketPlanResponse,
  ShoppingListResponse,
} from "../lib/types";
import { thisMondayIso } from "../lib/week";


// ----- local edit state (Phase 13c: simplified — no swap, no revise) -----
//
// The basket plan aggregates recipe shopping lists, which already hold the
// user's curated picks. Per-basket quantity tweaks and removes stay as
// ephemeral local state — they affect Copy, not the recipe's shopping list.

interface ItemEdit {
  quantity: number;
  removed: boolean;
}

type EditMap = Record<number, ItemEdit>;


function isValidIsoDate(iso: string | undefined): iso is string {
  return typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

function formatNeeded(qty: number, unit: string | null | undefined): string {
  const cleanQty = Number.isInteger(qty) ? qty.toString() : qty.toFixed(1);
  if (!unit) return cleanQty;
  const noSpace = ["g", "kg", "mg", "ml", "l", "cl"].includes(unit.toLowerCase());
  return noSpace ? `${cleanQty}${unit}` : `${cleanQty} ${unit}`;
}

function effective(item: BasketPlanItem, edit?: ItemEdit) {
  return {
    quantity: edit?.quantity ?? item.quantity,
    removed: edit?.removed ?? false,
  };
}


// ----- top-level page (route binding) -----

export function BasketPlanPage() {
  const { week_start } = useParams<{ week_start?: string }>();

  if (week_start !== undefined && !isValidIsoDate(week_start)) {
    return <Navigate to="/basket-plan" replace />;
  }

  const weekStartIso = week_start ?? thisMondayIso();
  return <BasketPlanView key={weekStartIso} weekStartIso={weekStartIso} />;
}


function BasketPlanView({ weekStartIso }: { weekStartIso: string }) {
  const qc = useQueryClient();

  const shoppingList = useQuery<ShoppingListResponse>({
    queryKey: ["shopping-list", weekStartIso],
    queryFn: () =>
      api.get<ShoppingListResponse>(
        `/api/meal-plans/${weekStartIso}/shopping-list`,
      ),
  });

  // GET the cached basket plan first; on 404 POST to generate a fresh one.
  // Post-13c this generation is cheap — no Claude calls; it just aggregates
  // each recipe's shopping list.
  const basketPlan = useQuery<BasketPlanResponse>({
    queryKey: ["basket-plan", weekStartIso],
    queryFn: async () => {
      try {
        return await api.get<BasketPlanResponse>(
          `/api/meal-plans/${weekStartIso}/basket-plan`,
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          return await api.post<BasketPlanResponse>(
            `/api/meal-plans/${weekStartIso}/basket-plan`,
          );
        }
        throw e;
      }
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
    gcTime: 5 * 60_000,
  });

  const regenerate = useMutation({
    mutationFn: () =>
      api.post<BasketPlanResponse>(
        `/api/meal-plans/${weekStartIso}/basket-plan`,
      ),
    onSuccess: (data) => {
      qc.setQueryData(["basket-plan", weekStartIso], data);
      setEdits({});
    },
  });

  const [edits, setEdits] = useState<EditMap>({});
  const showLoading = basketPlan.isPending || regenerate.isPending;

  return (
    <Shell>
      <BasketHeader
        weekStartIso={weekStartIso}
        response={basketPlan.data}
        regenerating={regenerate.isPending}
        onRegenerate={() => regenerate.mutate()}
        edits={edits}
      />

      {showLoading && <AggregatingNotice shoppingList={shoppingList.data} />}

      {basketPlan.isError && !regenerate.isPending && (
        <BasketPlanError error={basketPlan.error} />
      )}

      {basketPlan.data && !showLoading && (
        <ItemsList
          items={basketPlan.data.items}
          edits={edits}
          setEdits={setEdits}
        />
      )}
    </Shell>
  );
}


// ----- header -----

function BasketHeader({
  weekStartIso,
  response,
  regenerating,
  onRegenerate,
  edits,
}: {
  weekStartIso: string;
  response: BasketPlanResponse | undefined;
  regenerating: boolean;
  onRegenerate: () => void;
  edits: EditMap;
}) {
  return (
    <>
      <section className="mb-12 grid grid-cols-12 gap-8 border-b border-ink/80 pb-14">
        <div
          className="col-span-12 md:col-span-3 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="label mb-3 text-ink-walnut">Section</div>
          <div className="label text-sienna">N° 06</div>
        </div>
        <div
          className="col-span-12 md:col-span-9 animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          <h1 className="display-1">
            The <span className="display-italic">Basket</span>
          </h1>
          {response ? (
            <>
              <SummarySentence response={response} />
              <GenerationStamp
                generatedAt={response.generated_at}
                regenerating={regenerating}
                onRegenerate={onRegenerate}
                items={response.items}
                edits={edits}
              />
            </>
          ) : (
            <p className="mt-8 max-w-xl font-body text-xl italic leading-snug text-ink-walnut">
              Gathering the week's shopping lists from each recipe and merging
              duplicates by product.
            </p>
          )}
        </div>
      </section>

      <section
        className="mb-14 animate-fade-up"
        style={{ animationDelay: "220ms" }}
      >
        <WeekNavigator weekStartIso={weekStartIso} basePath="/basket-plan" />
      </section>
    </>
  );
}


function formatRelativeAge(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}


function GenerationStamp({
  generatedAt,
  regenerating,
  onRegenerate,
  items,
  edits,
}: {
  generatedAt: string;
  regenerating: boolean;
  onRegenerate: () => void;
  items: BasketPlanItem[];
  edits: EditMap;
}) {
  return (
    <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
      <span className="label text-ink-muted">
        Generated {formatRelativeAge(generatedAt)}
      </span>
      <button
        type="button"
        onClick={onRegenerate}
        disabled={regenerating}
        className="label text-sienna underline decoration-sienna/40 underline-offset-4 transition-colors hover:text-ink hover:decoration-ink/60 disabled:opacity-40"
      >
        {regenerating ? "regenerating…" : "regenerate"}
      </button>
      <span className="label text-ink-muted">·</span>
      <CopyAction items={items} edits={edits} />
    </div>
  );
}


function CopyAction({
  items,
  edits,
}: {
  items: BasketPlanItem[];
  edits: EditMap;
}) {
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  const validPicks = items
    .map((item, index) => {
      const eff = effective(item, edits[index]);
      if (item.error || !item.product_id || eff.removed) return null;
      return {
        index,
        ingredient_name: item.ingredient_text,
        product_id: item.product_id,
        product_name: item.product_name ?? "",
        quantity: eff.quantity,
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);

  const excludedCount = items.length - validPicks.length;
  const excludedNames = items
    .filter((item, idx) => {
      const eff = effective(item, edits[idx]);
      return item.error || !item.product_id || eff.removed;
    })
    .map((item) => item.ingredient_text)
    .join(", ");

  const handleCopy = async () => {
    const payload = validPicks.map((p) => ({
      productId: p.product_id,
      quantity: p.quantity,
      name: p.product_name || p.ingredient_name,
    }));
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
      setCopiedAt(Date.now());
      setTimeout(() => setCopiedAt(null), 4000);
    } catch {
      window.alert(
        "Could not copy to clipboard. Check your browser's clipboard permissions.",
      );
    }
  };

  if (copiedAt) {
    return (
      <span className="label text-sienna">
        ✓ copied {validPicks.length} — open the Maison extension
      </span>
    );
  }

  const label =
    excludedCount > 0
      ? `copy ${validPicks.length} picks (${excludedCount} excluded)`
      : `copy ${validPicks.length} picks`;

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={validPicks.length === 0}
      title={excludedCount > 0 ? `excluded: ${excludedNames}` : undefined}
      className="label text-sienna underline decoration-sienna/40 underline-offset-4 transition-colors hover:text-ink hover:decoration-ink/60 disabled:opacity-40"
    >
      {label}
    </button>
  );
}


function SummarySentence({ response }: { response: BasketPlanResponse }) {
  const total = response.items.length;
  const matched = response.items_with_matches;
  const needsSetup = response.items_without_matches;

  return (
    <p className="mt-8 max-w-2xl font-body text-xl italic leading-snug text-ink-walnut">
      <span className="font-display not-italic text-2xl text-ink">
        {matched}
      </span>{" "}
      product{matched === 1 ? "" : "s"} from{" "}
      <span className="font-display not-italic text-2xl text-ink">{total}</span>{" "}
      line{total === 1 ? "" : "s"}
      {needsSetup > 0 && (
        <>
          {" "}—{" "}
          <span className="font-display not-italic text-2xl text-sienna">
            {needsSetup}
          </span>{" "}
          need{needsSetup === 1 ? "s" : ""} setup on the recipe page
        </>
      )}
      .
    </p>
  );
}


// ----- loading notice (no kitchen ticker — aggregation is cheap now) -----

function AggregatingNotice({
  shoppingList,
}: {
  shoppingList: ShoppingListResponse | undefined;
}) {
  const totalLines = useMemo(() => {
    if (!shoppingList) return 0;
    return (
      shoppingList.from_recipes.length + shoppingList.from_regulars.length
    );
  }, [shoppingList]);

  // After 4 seconds show a "this shouldn't be long" fallback so the user
  // knows something's happening even on a slow regen.
  const [showExtraNote, setShowExtraNote] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowExtraNote(true), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Building your basket"
      className="mx-auto max-w-xl py-20 text-center"
    >
      <Ornament className="mx-auto mb-6 h-4 w-20 text-sienna" />
      <div className="label mb-6 text-sienna">In progress</div>
      <h2 className="font-display text-3xl italic text-ink">
        Aggregating the week's shopping lists
      </h2>
      {totalLines > 0 && (
        <p className="mt-4 font-body text-sm italic text-ink-walnut">
          {totalLines} lines from {shoppingList?.from_recipes.length ?? 0}{" "}
          recipe item{totalLines === 1 ? "" : "s"} plus regulars — merging by
          product.
        </p>
      )}
      {showExtraNote && (
        <p className="mt-6 font-body text-sm italic text-ink-muted">
          A non-fixed regular may be calling the matcher once. Should be quick.
        </p>
      )}
    </div>
  );
}


// ----- items list -----

function ItemsList({
  items,
  edits,
  setEdits,
}: {
  items: BasketPlanItem[];
  edits: EditMap;
  setEdits: React.Dispatch<React.SetStateAction<EditMap>>;
}) {
  const recipeRows = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.source === "recipe");
  const regularRows = items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.source === "regular");

  return (
    <div className="mt-8 space-y-16">
      {recipeRows.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-4">
            <div className="label text-sienna">N° 01</div>
            <div className="label text-ink-walnut">From the recipes</div>
            <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
          </div>
          <ol>
            {recipeRows.map(({ item, idx }, position) => (
              <ItemRow
                key={idx}
                item={item}
                index={idx}
                position={position + 1}
                edit={edits[idx]}
                setEdits={setEdits}
              />
            ))}
          </ol>
        </section>
      )}

      {regularRows.length > 0 && (
        <section>
          <div className="mb-6 flex items-center gap-4">
            <div className="label text-sienna">N° 02</div>
            <div className="label text-ink-walnut">From the regulars</div>
            <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
          </div>
          <ol>
            {regularRows.map(({ item, idx }, position) => (
              <ItemRow
                key={idx}
                item={item}
                index={idx}
                position={position + 1}
                edit={edits[idx]}
                setEdits={setEdits}
              />
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}


function ItemRow({
  item,
  index,
  position,
  edit,
  setEdits,
}: {
  item: BasketPlanItem;
  index: number;
  position: number;
  edit: ItemEdit | undefined;
  setEdits: React.Dispatch<React.SetStateAction<EditMap>>;
}) {
  const eff = effective(item, edit);
  const isError = Boolean(item.error);

  const handleQuantityChange = (raw: string) => {
    const n = parseInt(raw || "1", 10);
    const newQty = Number.isNaN(n) || n < 1 ? 1 : n;
    setEdits((prev) => ({
      ...prev,
      [index]: {
        quantity: newQty,
        removed: prev[index]?.removed ?? false,
      },
    }));
  };

  const handleToggleRemove = () => {
    setEdits((prev) => ({
      ...prev,
      [index]: {
        quantity: prev[index]?.quantity ?? item.quantity,
        removed: !(prev[index]?.removed ?? false),
      },
    }));
  };

  return (
    <li
      className={`group animate-fade-up ${isError ? "bg-sienna/[0.03]" : ""} ${
        eff.removed ? "opacity-40" : ""
      }`}
      style={{ animationDelay: `${Math.min(position * 30, 600)}ms` }}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 border-b border-ink/10 py-5">
        <div className="min-w-[3.5rem]">
          <span className="label text-ink-muted">
            {String(position).padStart(2, "0")}
          </span>
        </div>

        <div>
          <div className="font-body text-base text-ink-walnut">
            {item.ingredient_text}
          </div>

          {item.needed_quantity !== null && item.needed_quantity !== undefined && (
            <div className="label mt-1 text-ink-muted">
              needs ~{formatNeeded(item.needed_quantity, item.needed_unit)}
            </div>
          )}

          {item.source_count > 1 ? (
            <div
              className="label mt-1 text-ink-muted"
              title={item.source_lines.join("\n")}
            >
              merged from {item.source_count} lines
              {item.source_recipe_titles.length > 0 &&
                ` · ${item.source_recipe_titles.join(", ")}`}
            </div>
          ) : (
            item.recipe_title && (
              <div className="label mt-1 text-ink-muted">
                from {item.recipe_title}
              </div>
            )
          )}

          {isError ? (
            <NeedsSetupNotice item={item} />
          ) : (
            <div className="mt-3 font-display text-2xl text-ink">
              {item.product_name ?? "—"}
              {item.product_price !== null && item.product_price !== undefined && (
                <span className="ml-3 font-mono text-sm text-ink-walnut">
                  £{item.product_price.toFixed(2)}
                </span>
              )}
              {item.pack_size_value !== null &&
                item.pack_size_value !== undefined &&
                item.pack_size_unit && (
                  <span className="ml-2 font-mono text-xs text-ink-muted">
                    {Number.isInteger(item.pack_size_value)
                      ? `${item.pack_size_value}${item.pack_size_unit}`
                      : `${item.pack_size_value.toFixed(0)}${item.pack_size_unit}`}
                  </span>
                )}
            </div>
          )}
        </div>

        {!isError && (
          <div className="flex flex-col items-end gap-2">
            <input
              type="number"
              min={1}
              value={eff.quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              disabled={eff.removed}
              aria-label="quantity"
              className={`w-14 border-0 border-b border-ink/30 bg-transparent py-1 text-right font-display text-xl text-ink focus:border-ink focus:outline-none ${
                eff.removed ? "opacity-30" : ""
              }`}
            />
            <button
              type="button"
              onClick={handleToggleRemove}
              className="font-mono text-[10px] uppercase tracking-widest text-ink-muted transition-colors hover:text-sienna"
              aria-label={eff.removed ? "put back" : "remove from basket"}
            >
              {eff.removed ? "undo" : "× remove"}
            </button>
          </div>
        )}
      </div>
    </li>
  );
}


function NeedsSetupNotice({ item }: { item: BasketPlanItem }) {
  return (
    <div className="mt-3">
      <div className="font-body text-base italic text-sienna">
        — {item.error ?? "needs setup"}
      </div>
      {item.recipe_id && (
        <Link
          to={`/recipes/${item.recipe_id}`}
          className="ink-link label mt-3 inline-block text-sienna"
        >
          Open the recipe ↗
        </Link>
      )}
    </div>
  );
}


// ----- error state -----

function BasketPlanError({ error }: { error: unknown }) {
  const message = error instanceof ApiError ? error.detail : String(error);
  return (
    <div className="mx-auto max-w-2xl py-16">
      <div className="border-l-2 border-sienna pl-6">
        <div className="label mb-2 text-sienna">
          Could not build the basket
        </div>
        <p className="font-body text-base italic text-ink-walnut">{message}</p>
        <p className="mt-4 font-body text-sm italic text-ink-muted">
          Most commonly: a recipe in this week's plan doesn't have a shopping
          list yet. Open the recipe and click Generate shopping list.
        </p>
      </div>
    </div>
  );
}
